import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { CheckCircle2, LogIn, UserPlus } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";

export type PhoneConflictState = "confirm" | "emailSent" | null;

interface Props {
  email: string;
  error?: string;
  loading?: boolean;
  open: boolean;
  state: PhoneConflictState;
  onCreateNewDigitalAccount: () => void;
  onSignInToExistingAccount: () => void;
}

export default function PhoneConflictDialog({
  email,
  error,
  loading = false,
  open,
  state,
  onCreateNewDigitalAccount,
  onSignInToExistingAccount,
}: Props) {
  const isMobile = useIsMobile();
  const emailSent = state === "emailSent";

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="phone-conflict-title"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "8px",
        },
      }}
    >
      <DialogTitle id="phone-conflict-title" sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "#DDF5EF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#08765C",
              flexShrink: 0,
            }}
          >
            {emailSent ? <CheckCircle2 size={26} /> : <UserPlus size={26} />}
          </Box>
          <Typography variant="h3" sx={{ lineHeight: 1.2 }}>
            {emailSent ? "Check your email" : "Choose how to continue"}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: "12px" }}>
        <Typography
          variant="body"
          sx={{ color: "#4F4F4F", display: "block", lineHeight: 1.45 }}
        >
          {emailSent ? (
            <>
              We sent a secure link to{" "}
              <Box component="span" sx={{ color: "#08765C", fontWeight: 700 }}>
                {email}
              </Box>
              . Open it to finish creating your digital account.
            </>
          ) : (
            <>
              We found an existing Faye account connected to this phone number.
              You can create a new digital account, or sign in to the existing
              account.
            </>
          )}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ marginTop: "16px" }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: "stretch",
          flexDirection: "column",
          gap: emailSent ? "4px" : "12px",
          padding: "0 24px 24px",
          "& > :not(style) ~ :not(style)": {
            marginLeft: 0,
          },
        }}
      >
        {emailSent && (
          <Typography variant="caption" sx={{ color: "#4F4F4F", mb: "4px" }}>
            Didn&apos;t receive it?
          </Typography>
        )}
        <Button
          variant={emailSent ? "text" : "contained"}
          color="primary"
          size={emailSent ? "small" : isMobile ? "medium" : "large"}
          startIcon={
            emailSent ? <CheckCircle2 size={16} /> : <UserPlus size={20} />
          }
          disabled={loading}
          onClick={onCreateNewDigitalAccount}
          sx={
            emailSent
              ? { justifyContent: "flex-start" }
              : { borderRadius: "999px", minHeight: "56px", fontWeight: 700 }
          }
        >
          {emailSent ? "Resend secure link" : "Create new digital account"}
        </Button>
        <Button
          variant={emailSent ? "text" : "outlined"}
          color="primary"
          size={emailSent ? "small" : isMobile ? "medium" : "large"}
          startIcon={<LogIn size={emailSent ? 16 : 20} />}
          disabled={loading}
          onClick={onSignInToExistingAccount}
          sx={
            emailSent
              ? { justifyContent: "flex-start" }
              : {
                  borderRadius: "999px",
                  minHeight: "56px",
                  fontWeight: 700,
                  background: "#FFFFFF",
                }
          }
        >
          Sign in to existing account
        </Button>
      </DialogActions>
    </Dialog>
  );
}
